import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../api/api";
import Swal from "sweetalert2";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { motion, AnimatePresence } from "framer-motion";
import AgendamentosCalendar from "../components/AgendamentosCalendar";
import CustomSelect from "../components/CustomSelect";
import PainelDatePicker from "../components/PainelDatePicker";
import FilaZeroTimeSlotPicker from "../components/FilaZeroTimeSlotPicker";
import horariosAtendimento from "../api/horariosAtendimento";
import dayjs from "dayjs";
import useAutoRefresh from "../hooks/useAutoRefresh";
import { buildPaginationItems } from "../utils/pagination";
import { obterEstabelecimentoAtivoId } from "../utils/estabelecimentoContext";
import {
  Calendar,
  ChevronDown,
  ChevronUp,
  SlidersHorizontal,
  Search,
  X,
  Edit,
  Trash2,
  Plus,
  Save,
} from "lucide-react";

const normalizeTimeValue = (value) => String(value || "").slice(0, 5);

const normalizeStatusValue = (status) =>
  String(status || "livre")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const isStatusTerminal = (status) => {
  const normalized = normalizeStatusValue(status);
  return normalized === "finalizado" || normalized === "cancelado";
};

const normalizeTimeSlots = (times = [], statusTimes = []) => {
  const source =
    Array.isArray(statusTimes) && statusTimes.length
      ? statusTimes
      : (Array.isArray(times) ? times : []).map((hora) => ({
          hora,
          status: "livre",
        }));

  return source
    .map((item) =>
      typeof item === "string"
        ? { hora: normalizeTimeValue(item), status: "livre" }
        : {
            ...(item && typeof item === "object" ? item : {}),
            hora: normalizeTimeValue(item?.hora),
            status: normalizeStatusValue(item?.status),
          }
    )
    .filter((item) => item.hora);
};

const Agendamentos = () => {
  const [agendamentos, setAgendamentos] = useState([]);
  const [busca, setBusca] = useState("");
  const [statusFiltro, setStatusFiltro] = useState("todos");
  const [sortKey, setSortKey] = useState("data");
  const [sortDir, setSortDir] = useState("desc");
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [dataFiltroInicio, setDataFiltroInicio] = useState("");
  const [dataFiltroFim, setDataFiltroFim] = useState("");
  const [presetFiltro, setPresetFiltro] = useState("custom");
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [agendamentoSelecionado, setAgendamentoSelecionado] = useState(null);
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");
  const [modoVisualizacao, setModoVisualizacao] = useState("calendario");
  const [filtrosAbertos, setFiltrosAbertos] = useState(false);
  const [filtroDataPickerAberto, setFiltroDataPickerAberto] = useState(false);
  const [mostrarEncerradosCalendario, setMostrarEncerradosCalendario] = useState(false);
  const [estabelecimentoCalendarioId, setEstabelecimentoCalendarioId] = useState(
    () => obterEstabelecimentoAtivoId()
  );

  useEffect(() => {
    const atualizarEstabelecimento = () => {
      setEstabelecimentoCalendarioId(obterEstabelecimentoAtivoId());
    };

    window.addEventListener("focus", atualizarEstabelecimento);
    window.addEventListener("storage", atualizarEstabelecimento);

    return () => {
      window.removeEventListener("focus", atualizarEstabelecimento);
      window.removeEventListener("storage", atualizarEstabelecimento);
    };
  }, []);

  // Paginação
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [limite, setLimite] = useState(10);
  const [totalAgendamentos, setTotalAgendamentos] = useState(0);

  // -------- NOVO AGENDAMENTO (modal) --------
  const [showNovo, setShowNovo] = useState(false);
  const [novoLoading, setNovoLoading] = useState(false);
  const [optsLoading, setOptsLoading] = useState(false);
  const [estabelecimentos, setEstabelecimentos] = useState([]);
  const [profissionais, setProfissionais] = useState([]);
  const [servicos, setServicos] = useState([]);
  const [novoAgendamento, setNovoAgendamento] = useState({
    nome_cliente: "",
    estabelecimento_id: "",
    profissional_id: "",
    servico_id: "",
    data: "",
    hora: "",
    status: "pendente",
    observacoes: "",
  });
  const [erros, setErros] = useState({});

  const [horasDisponiveis, setHorasDisponiveis] = useState([]);
  const [horasLoading, setHorasLoading] = useState(false);

  // -------- EDIÇÃO (drawer): data/hora baseadas em horários configurados --------
  const [editData, setEditData] = useState("");
  const [editHora, setEditHora] = useState("");
  const [editHorasDisponiveis, setEditHorasDisponiveis] = useState([]);
  const [editHorasLoading, setEditHorasLoading] = useState(false);
  const [editOriginal, setEditOriginal] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const today = dayjs().format("YYYY-MM-DD");

  // Util: spinner simples
  const Spinner = ({ size = 16 }) => (
    <span
      style={{
        display: "inline-block",
        width: size,
        height: size,
        border: "2px solid rgba(0,0,0,0.15)",
        borderTopColor: "var(--primary)",
        borderRadius: "50%",
        animation: "spin 0.6s linear infinite",
      }}
    />
  );
  useEffect(() => {
    if (document.getElementById("spin-keyframes")) return;
    const style = document.createElement("style");
    style.id = "spin-keyframes";
    style.innerHTML = `
      @keyframes spin { to { transform: rotate(360deg); } }
      .input-error { border: 1px solid #ef4444 !important; box-shadow: 0 0 0 3px rgba(239,68,68,.15); }
      .error-text { color: #ef4444; font-size: 12px; margin-top: 6px; }
      .modal-title { font-weight: 800; font-size: 18px; }
      .modal-subtitle { color: var(--muted); font-size: 13px; margin-top: 4px; }
      .field-label { font-weight: 700; font-size: 13px; margin-bottom: 6px; display:block; }
      .field { width: 100%; padding: 10px 12px; border: 1px solid var(--line); border-radius: 10px; background: var(--card); color: var(--text); }
      .field-textarea { resize: vertical; min-height: 88px; line-height: 1.5; font-family: inherit; }
      .field:focus { outline: none; box-shadow: 0 0 0 3px rgba(37,99,235,.15); border-color: var(--primary); }
      .modal-card { width: 680px; max-width: calc(100vw - 32px); max-height: calc(100vh - 32px); display: flex; flex-direction: column; background: var(--card); border-radius: 16px; box-shadow: var(--shadow); overflow: hidden; }
      .modal-scroll { overflow-y: auto; overscroll-behavior: contain; -webkit-overflow-scrolling: touch; padding-right: 2px; }
      .filter-date-input { width: 100%; min-height: 34px; font-size: 15px; }
      .resumo-grid { display: flex; justify-content: space-between; align-items: flex-end; gap: 12px; margin-top: 14px; }
      .modal-actions { display:flex; gap: 10px; justify-content: flex-end; padding-top: 6px; }
      .btn-secondary { border: 1px solid var(--line); background: #f3f4f6; color: var(--text); border-radius: 10px; padding: 10px 14px; font-weight: 600; }
      .btn-secondary:hover { background: #e5e7eb; }
      .section-split { height: 1px; background: var(--line); margin: 12px 0; }
      .two-cols { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
      .three-cols { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px; }
      @media (max-width: 768px) {
        .two-cols, .three-cols { grid-template-columns: 1fr; }
        .modal-card { max-width: calc(100vw - 16px); max-height: calc(100vh - 16px); }
        .modal-scroll { padding-right: 0; }
        .resumo-grid { flex-direction: column; align-items: stretch; }
        .date-filter-field { min-height: 44px; cursor: pointer; }
        .date-filter-field input { font-size: 16px; }
        .modal-actions { flex-direction: column-reverse; align-items: stretch; }
        .modal-actions > * { width: 100%; }
      }
    `;
    document.head.appendChild(style);
  }, []);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const isDark = document.body.classList.contains("dark");
      setTheme(isDark ? "dark" : "light");
    });
    observer.observe(document.body, { attributes: true });
    return () => observer.disconnect();
  }, []);

  const carregarAgendamentos = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const params = new URLSearchParams();
      const isCalendarView = modoVisualizacao === "calendario";
      params.set("page", isCalendarView ? "1" : String(paginaAtual));
      params.set("limit", isCalendarView ? "500" : String(limite));
      if (dataFiltroInicio) params.set("dataInicio", dataFiltroInicio);
      if (dataFiltroFim) params.set("dataFim", dataFiltroFim);
      if (busca.trim()) params.set("busca", busca.trim());
      if (statusFiltro !== "todos") params.set("status", statusFiltro);

      const queryString = params.toString();
      const { data } = await api.get(`/agendamentos${queryString ? `?${queryString}` : ""}`);
      // Ordenar por data_horario em ordem decrescente (mais recentes primeiro)
      const resultados = data?.resultados || [];
      resultados.sort((a, b) => new Date(b.data_horario) - new Date(a.data_horario));
      setAgendamentos(resultados);
      setTotalPaginas(data.totalPaginas || 1);
      setTotalAgendamentos(data?.total || data?.resultados?.length || 0);
    } catch (err) {
      console.error("Erro ao carregar agendamentos:", err);
      if (!silent) toast.error("Erro ao carregar agendamentos!");
    } finally {
      if (!silent) setLoading(false);
    }
  }, [
    paginaAtual,
    limite,
    modoVisualizacao,
    dataFiltroInicio,
    dataFiltroFim,
    busca,
    statusFiltro,
  ]);

  useEffect(() => {
    carregarAgendamentos(false);
  }, [paginaAtual, limite, carregarAgendamentos]);

  useEffect(() => {
    setPaginaAtual(1);
  }, [busca, statusFiltro]);

  useEffect(() => {
    const handler = () => carregarAgendamentos(true);
    window.addEventListener("fz:agendamento", handler);
    return () => window.removeEventListener("fz:agendamento", handler);
  }, [carregarAgendamentos]);

  useAutoRefresh(() => carregarAgendamentos(true), 10000, !drawerOpen && !showNovo);

  const formatarData = (data) => {
    if (!data) return "";
    try {
      return dayjs(data).format('DD/MM/YY - HH:mm');
    } catch (e) {
      return String(data);
    }
  };

  const normalizarAgendamentoEdicao = (agendamento = {}) => {
    const estabelecimentoPadrao = localStorage.getItem("estabelecimento_id") || "";
    return {
      ...agendamento,
      estabelecimento_id:
        agendamento.estabelecimento_id ||
        agendamento.estabelecimento?.id ||
        (estabelecimentoPadrao ? Number(estabelecimentoPadrao) : ""),
    };
  };

  const prepararEdicao = (agendamento) => {
    const normalizado = normalizarAgendamentoEdicao(agendamento);
    const dataHorario = dayjs(normalizado.data_horario);
    setAgendamentoSelecionado(normalizado);
    setEditOriginal(normalizado);
    setEditData(dataHorario.isValid() ? dataHorario.format("YYYY-MM-DD") : "");
    setEditHora(dataHorario.isValid() ? dataHorario.format("HH:mm") : "");
  };

  const abrirDrawer = (agendamento) => {
    if (!agendamento) return;

    prepararEdicao(normalizarAgendamentoEdicao(agendamento));
    setDrawerOpen(true);
  };

  useEffect(() => {
    const carregarHorasEdicao = async () => {
      if (!drawerOpen || !agendamentoSelecionado) return;
      if (isStatusTerminal(agendamentoSelecionado.status)) {
        setEditHorasDisponiveis([]);
        return;
      }
      if (!editData) {
        setEditHorasDisponiveis([]);
        return;
      }

      const estabelecimentoId =
        agendamentoSelecionado.estabelecimento_id ||
        localStorage.getItem("estabelecimento_id") ||
        "";

      const profissionalId =
        agendamentoSelecionado.profissional_id || agendamentoSelecionado.profissional?.id || "";

      if (!estabelecimentoId || !profissionalId) {
        setEditHorasDisponiveis([]);
        return;
      }

      setEditHorasLoading(true);
      try {
        const resp = await horariosAtendimento.listarDisponiveisPorData(
          Number(estabelecimentoId),
          editData,
          Number(profissionalId),
          agendamentoSelecionado.id,
          agendamentoSelecionado.servico_id || agendamentoSelecionado.servico?.id
        );

        const listaNormalizada = normalizeTimeSlots(
          resp.horarios,
          resp.horarios_status
        );
        const horaAtual = normalizeTimeValue(editHora);
        const horasLista = listaNormalizada.map((item) => item.hora);
        setEditHorasDisponiveis(
          horaAtual && !horasLista.includes(horaAtual)
            ? [...listaNormalizada, { hora: horaAtual, status: "atual" }]
            : listaNormalizada
        );
      } catch (err) {
        console.error("Erro ao carregar horários disponíveis (edição):", err);
        setEditHorasDisponiveis([]);
        toast.error(err?.mensagem || err?.message || "Erro ao carregar horários disponíveis.");
      } finally {
        setEditHorasLoading(false);
      }
    };

    carregarHorasEdicao();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawerOpen, agendamentoSelecionado?.id, agendamentoSelecionado?.status, editData]);

  const fecharDrawer = () => {
    setDrawerOpen(false);
    setTimeout(() => {
      setAgendamentoSelecionado(null);
      setEditOriginal(null);
      setEditData("");
      setEditHora("");
      setEditHorasDisponiveis([]);
    }, 200);
  };

  const confirmarCancelamento = (id) => {
    Swal.fire({
      title: "Tem certeza",
      text: "Essa ação não pode ser desfeita.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Sim, cancelar!",
      cancelButtonText: "Não",
      background: theme === "dark" ? "#1e293b" : "#fff",
      color: theme === "dark" ? "#f8fafc" : "#0f172a",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.put(`/agendamentos/${id}/cancelar`);
          toast.info("Agendamento cancelado!");
          carregarAgendamentos();
        } catch (err) {
          console.error("Erro ao cancelar agendamento:", err);
          toast.error("Erro ao cancelar agendamento!");
        }
      }
    });
  };

  const removerAgendamento = (id) => {
    Swal.fire({
      title: "Excluir definitivamente",
      text: "Essa ação removerá o agendamento do sistema.",
      icon: "error",
      showCancelButton: true,
      confirmButtonColor: "#b91c1c",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Sim, remover!",
      cancelButtonText: "Cancelar",
      background: theme === "dark" ? "#1e293b" : "#fff",
      color: theme === "dark" ? "#f8fafc" : "#0f172a",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.delete(`/agendamentos/${id}/remover`);
          toast.success("Agendamento removido!");
          carregarAgendamentos();
          fecharDrawer();
        } catch (err) {
          console.error("Erro ao remover agendamento:", err);
          toast.error("Erro ao remover agendamento!");
        }
      }
    });
  };

  const salvarAlteracoes = async () => {
    if (!agendamentoSelecionado || editSaving) return;

    const agendamentoOriginal =
      editOriginal ||
      agendamentos.find(
        (agendamento) =>
          String(agendamento.id) === String(agendamentoSelecionado.id)
      ) ||
      agendamentoSelecionado;

    const statusOriginal = normalizeStatusValue(agendamentoOriginal.status);
    const statusSelecionado = normalizeStatusValue(agendamentoSelecionado.status);
    if (statusOriginal === "finalizado") {
      toast.info("Agendamento finalizado não pode ser alterado.");
      return;
    }

    const normalizedEditHour = normalizeTimeValue(editHora);

    if (!editData || !normalizedEditHour) {
      toast.error("Selecione a data e o horário do agendamento.");
      return;
    }

    try {
      const originalData = agendamentoOriginal.data_horario;
      const dataHorarioAtual = `${editData}T${normalizedEditHour}:00`;
      const originalNorm = originalData
        ? dayjs(originalData).format("YYYY-MM-DDTHH:mm:ss")
        : "";
      const newNorm = dayjs(dataHorarioAtual).isValid()
        ? dayjs(dataHorarioAtual).format("YYYY-MM-DDTHH:mm:ss")
        : "";
      const dataHoraAlterada = Boolean(newNorm && newNorm !== originalNorm);

      if (dataHoraAlterada && dayjs(dataHorarioAtual).isBefore(dayjs())) {
        toast.error(
          "Não é possível alterar o agendamento para uma data ou horário no passado."
        );
        return;
      }

      const dados = {};
      if (
        dataHoraAlterada &&
        !isStatusTerminal(statusOriginal) &&
        !isStatusTerminal(statusSelecionado)
      ) {
        dados.data_horario = dataHorarioAtual;
      }
      if (
        !isStatusTerminal(statusOriginal) &&
        (agendamentoSelecionado.status || "") !==
        (agendamentoOriginal.status || "")
      ) {
        dados.status = agendamentoSelecionado.status;
      }
      if (
        (agendamentoSelecionado.observacoes || "") !==
        (agendamentoOriginal.observacoes || "")
      ) {
        dados.observacoes = agendamentoSelecionado.observacoes || "";
      }

      if (statusOriginal === "cancelado" && dados.data_horario) {
        toast.error("Agendamento cancelado: data e horário não podem ser alterados.");
        return;
      }

      if (!Object.keys(dados).length) {
        toast.info("Nenhuma alteração para salvar.");
        return;
      }

      setEditSaving(true);
      await api.put(`/agendamentos/${agendamentoSelecionado.id}`, dados);
      toast.success("Agendamento atualizado com sucesso!");
      await carregarAgendamentos(true);
      fecharDrawer();
    } catch (err) {
      console.error("Erro ao atualizar agendamento:", err);
      toast.error(
        err.response?.data?.message ||
          err.response?.data?.erro ||
          "Erro ao atualizar!"
      );
    } finally {
      setEditSaving(false);
    }
  };

  const statusOrder = {
    pendente: 0,
    confirmado: 1,
    finalizado: 2,
    cancelado: 3,
  };

  const getStatusRank = (status) => {
    const key = (status || "").toLowerCase();
    return Number.isInteger(statusOrder[key]) ? statusOrder[key] : 99;
  };

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDir("asc");
  };

  const renderSortIndicator = (key) => {
    if (sortKey !== key) return "";
    return (
      <span style={{ marginLeft: 6, display: "inline-flex", alignItems: "center" }}>
        {sortDir === "asc" ? (
          <ChevronUp size={14} />
        ) : (
          <ChevronDown size={14} />
        )}
      </span>
    );
  };

  const aplicarFiltroData = ({ inicio, fim }) => {
    if (inicio && fim && dayjs(inicio).isAfter(dayjs(fim))) {
      toast.error("A data inicial não pode ser maior que a data final.");
      return;
    }

    setPaginaAtual(1);
    setDataInicio(inicio || "");
    setDataFim(fim || "");
    setDataFiltroInicio(inicio || "");
    setDataFiltroFim(fim || "");
    setSortKey("data");
    setSortDir("desc");
  };

  const getPresetRange = (preset) => {
    const hoje = dayjs();
    if (preset === "today") {
      const d = hoje.format("YYYY-MM-DD");
      return { inicio: d, fim: d };
    }
    if (preset === "thisMonth") {
      return {
        inicio: hoje.startOf("month").format("YYYY-MM-DD"),
        fim: hoje.endOf("month").format("YYYY-MM-DD"),
      };
    }
    if (preset === "lastMonth") {
      return {
        inicio: hoje.subtract(1, "month").startOf("month").format("YYYY-MM-DD"),
        fim: hoje.subtract(1, "month").endOf("month").format("YYYY-MM-DD"),
      };
    }
    if (preset === "february") {
      return {
        inicio: hoje.month(1).startOf("month").format("YYYY-MM-DD"),
        fim: hoje.month(1).endOf("month").format("YYYY-MM-DD"),
      };
    }
    if (preset === "december") {
      return {
        inicio: hoje.month(11).startOf("month").format("YYYY-MM-DD"),
        fim: hoje.month(11).endOf("month").format("YYYY-MM-DD"),
      };
    }
    return { inicio: "", fim: "" };
  };

  const getHeaderStyle = (key) => ({
    cursor: "pointer",
    color: sortKey === key ? "var(--primary)" : "inherit",
    userSelect: "none",
  });

  const listaFiltrada = agendamentos.filter((ag) => {
    const termo = busca.toLowerCase();
    const id = String(ag.id || "").toLowerCase();
    const cliente = (ag.nome_cliente || ag.cliente?.nome || "").toLowerCase();
    const profissional = (ag.profissional?.nome || "").toLowerCase();
    const servico = (ag.servico?.nome || "").toLowerCase();
    const status = String(ag.status || "").toLowerCase();
    const buscaOk =
      id.includes(termo) ||
      cliente.includes(termo) ||
      profissional.includes(termo) ||
      servico.includes(termo);
    const statusOk = statusFiltro === "todos" ? true : status === statusFiltro;
    const encerradosOk =
      modoVisualizacao !== "calendario" ||
      mostrarEncerradosCalendario ||
      (status !== "cancelado" && status !== "finalizado");

    return buscaOk && statusOk && encerradosOk;
  });

  const listaOrdenada = [...listaFiltrada].sort((a, b) => {
    const da = dayjs(a.data_horario).valueOf() || 0;
    const db = dayjs(b.data_horario).valueOf() || 0;

    // quando ordenar por data, ela e o criterio principal
    if (sortKey === "data") {
      if (da !== db) return sortDir === "asc" ? da - db : db - da;
      return 0;
    }

    // criterio principal: coluna selecionada
    let cmp = 0;
    if (sortKey === "id") {
      const va = Number(a.id) || 0;
      const vb = Number(b.id) || 0;
      cmp = va - vb;
    } else if (sortKey === "cliente") {
      const va = (a.nome_cliente || a.cliente?.nome || "").toLowerCase();
      const vb = (b.nome_cliente || b.cliente?.nome || "").toLowerCase();
      cmp = va.localeCompare(vb);
    } else if (sortKey === "servico") {
      const va = (a.servico?.nome || "").toLowerCase();
      const vb = (b.servico?.nome || "").toLowerCase();
      cmp = va.localeCompare(vb);
    } else if (sortKey === "status") {
      const va = getStatusRank(a.status);
      const vb = getStatusRank(b.status);
      cmp = va - vb;
    }

    if (cmp !== 0) return sortDir === "asc" ? cmp : -cmp;

    // desempate: data mais recente primeiro
    if (da !== db) return db - da;
    return 0;
  });

  const inicio = (paginaAtual - 1) * limite + 1;
  const fim = Math.min(inicio + limite - 1, totalAgendamentos);
  const paginationItems = buildPaginationItems(paginaAtual, totalPaginas);

  // ---------- Novo agendamento: helpers ----------
  const abrirModalNovo = async (preset = {}) => {
    setErros({});
    // Carrega opções primeiro para garantir que select de estabelecimentos esteja preenchido
    const estList = await carregarOpcoesSelects();
    const estabelecimentoPadrao = localStorage.getItem('estabelecimento_id');
    const estabelecimentoValor = estabelecimentoPadrao
      ? Number(estabelecimentoPadrao)
      : estList && estList.length === 1
        ? Number(estList[0].id)
      : "";
    setNovoAgendamento({
      nome_cliente: "",
      estabelecimento_id: estabelecimentoValor,
      profissional_id: "",
      servico_id: "",
      data: preset.data || "",
      hora: preset.hora || "",
      status: "pendente",
      observacoes: "",
    });
    setHorasDisponiveis([]);
    setShowNovo(true);
  };

  const fecharModalNovo = () => {
    setShowNovo(false);
  };

  const normalizaResultado = (data) => (Array.isArray(data) ? data : data?.resultados || []);

  const carregarOpcoesSelects = async () => {
    try {
      setOptsLoading(true);
      const estRes = await api.get("/estabelecimentos");
      const estNormalizado = normalizaResultado(estRes.data);
      setEstabelecimentos(estNormalizado);
      // não carregar profissionais/serviços globais aqui - serão carregados por estabelecimento ao selecionar
      setProfissionais([]);
      setServicos([]);
      return estNormalizado;
    } catch (e) {
      console.error("Erro ao carregar opções:", e);
      toast.error("Erro ao carregar opções para o formulário.");
      return [];
    } finally {
      setOptsLoading(false);
    }
  };

  // Quando estabelecer selecionado mudar, carregar profissionais e serviços ativos do estabelecimento
  useEffect(() => {
    const carregarPorEstabelecimento = async () => {
      const estId = novoAgendamento.estabelecimento_id;
      if (!showNovo) return;
      if (!estId) {
        setProfissionais([]);
        setServicos([]);
        return;
      }

      // reset dependências
      setNovoAgendamento((s) =>
        s.profissional_id || s.servico_id
          ? { ...s, profissional_id: "", servico_id: "" }
          : s
      );
      setHorasDisponiveis([]);

      try {
        setOptsLoading(true);
        const [profRes, servRes] = await Promise.all([
          api.get(`/profissionais?estabelecimentoId=${estId}&ativo=1`),
          api.get(`/servicos?estabelecimentoId=${estId}&ativo=1`),
        ]);
        setProfissionais(normalizaResultado(profRes.data));
        setServicos(normalizaResultado(servRes.data));
      } catch (err) {
        console.error('Erro ao carregar profissionais/serviços por estabelecimento:', err);
        setProfissionais([]);
        setServicos([]);
        toast.error('Erro ao carregar profissionais ou serviços para o estabelecimento.');
      } finally {
        setOptsLoading(false);
      }
    };

    carregarPorEstabelecimento();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [novoAgendamento.estabelecimento_id, showNovo]);

  const validarNovo = () => {
    const e = {};
    if (!novoAgendamento.nome_cliente || novoAgendamento.nome_cliente.trim().length < 3) {
      e.nome_cliente = "Informe o nome do cliente (mín. 3 caracteres)";
    }
    if (!novoAgendamento.estabelecimento_id)
      e.estabelecimento_id = "Selecione o estabelecimento";
    if (!novoAgendamento.profissional_id)
      e.profissional_id = "Selecione o profissional";
    if (!novoAgendamento.servico_id) e.servico_id = "Selecione o serviço";
    if (!novoAgendamento.data) e.data = "Informe a data";
    if (!novoAgendamento.hora) e.hora = "Selecione a hora";
    setErros(e);
    return Object.keys(e).length === 0;
  };

  const validarCampo = (field) => {
    const value = novoAgendamento[field];
    const e = { ...erros };
    if (field === "nome_cliente") {
      if (!value || value.trim().length < 3) e.nome_cliente = "Informe o nome do cliente (mín. 3 caracteres)";
      else delete e.nome_cliente;
    }
    if (field === "estabelecimento_id") {
      if (!value) e.estabelecimento_id = "Selecione o estabelecimento";
      else delete e.estabelecimento_id;
    }
    if (field === "profissional_id") {
      if (!value) e.profissional_id = "Selecione o profissional";
      else delete e.profissional_id;
    }
    if (field === "servico_id") {
      if (!value) e.servico_id = "Selecione o serviço";
      else delete e.servico_id;
    }
    if (field === "data") {
      if (!value) e.data = "Informe a data";
      else delete e.data;
    }
    if (field === "hora") {
      if (!value) e.hora = "Selecione a hora";
      else delete e.hora;
    }
    setErros(e);
  };

  useEffect(() => {
    const carregarHorasDisponiveis = async () => {
      if (!showNovo) return;
        if (!novoAgendamento.estabelecimento_id || !novoAgendamento.data || !novoAgendamento.profissional_id || !novoAgendamento.servico_id) {
        setHorasDisponiveis([]);
        return;
      }

      setHorasLoading(true);
      try {
        const resp = await horariosAtendimento.listarDisponiveisPorData(
          Number(novoAgendamento.estabelecimento_id),
          novoAgendamento.data,
          novoAgendamento.profissional_id ? Number(novoAgendamento.profissional_id) : undefined,
          undefined,
          novoAgendamento.servico_id ? Number(novoAgendamento.servico_id) : undefined
        );

        const listaNormalizada = normalizeTimeSlots(
          resp.horarios,
          resp.horarios_status
        );
        setHorasDisponiveis(listaNormalizada);

        const horasLista = listaNormalizada.map((item) => item.hora);
        const horaSelecionada = normalizeTimeValue(novoAgendamento.hora);

        // Se a hora selecionada não existir mais para essa data, limpa
        if (horaSelecionada && !horasLista.includes(horaSelecionada)) {
          setNovoAgendamento((s) => ({ ...s, hora: "" }));
        }
      } catch (err) {
        console.error("Erro ao carregar horários disponíveis:", err);
        setHorasDisponiveis([]);
        toast.error(err?.mensagem || err?.message || "Erro ao carregar horários disponíveis.");
      } finally {
        setHorasLoading(false);
      }
    };

    carregarHorasDisponiveis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showNovo, novoAgendamento.estabelecimento_id, novoAgendamento.data, novoAgendamento.profissional_id, novoAgendamento.servico_id]);

  const salvarNovoAgendamento = async () => {
    if (!validarNovo()) return;
    setNovoLoading(true);
    try {
      const normalizedHour = normalizeTimeValue(novoAgendamento.hora);
      const data_horario = `${novoAgendamento.data}T${normalizedHour}:00`;
      await api.post("/agendamentos", {
        nome_cliente: novoAgendamento.nome_cliente,
        estabelecimento_id: Number(novoAgendamento.estabelecimento_id),
        profissional_id: Number(novoAgendamento.profissional_id),
        servico_id: Number(novoAgendamento.servico_id),
        data_horario,
        status: novoAgendamento.status,
        observacoes: novoAgendamento.observacoes || "",
      });
      toast.success("Agendamento criado com sucesso!");
      setShowNovo(false);
      carregarAgendamentos();
    } catch (e) {
      console.error("Erro ao criar agendamento:", e);
      const msg =
        e.response?.data?.message ||
        e.response?.data?.mensagem ||
        e?.message ||
        "Erro ao criar agendamento.";
      toast.error(msg);
    } finally {
      setNovoLoading(false);
    }
  };

  // estilos de erro helper
  const errClass = (field) => (erros[field] ? "field input-error" : "field");

  const isNovoValido =
    novoAgendamento.estabelecimento_id &&
    novoAgendamento.profissional_id &&
    novoAgendamento.servico_id &&
    novoAgendamento.data &&
    novoAgendamento.hora &&
    novoAgendamento.nome_cliente &&
    novoAgendamento.nome_cliente.trim().length >= 3;

  const resumoProfissional =
    profissionais.find((p) => String(p.id) === String(novoAgendamento.profissional_id))?.nome || "-";
  const resumoServico =
    servicos.find((s) => String(s.id) === String(novoAgendamento.servico_id))?.nome || "-";
  const resumoDataHora =
    novoAgendamento.data && novoAgendamento.hora
      ? dayjs(`${novoAgendamento.data}T${novoAgendamento.hora}`).format("DD/MM/YYYY - HH:mm")
      : "-";

  const editHorasComAtual = useMemo(() => {
    const normalizedSlots = normalizeTimeSlots([], editHorasDisponiveis);
    const normalizedEditHour = normalizeTimeValue(editHora);

    if (!normalizedEditHour) return normalizedSlots;
    if (normalizedSlots.some((item) => item.hora === normalizedEditHour)) {
      return normalizedSlots;
    }

    return [
      ...normalizedSlots,
      { hora: normalizedEditHour, status: "atual" },
    ];
  }, [editHora, editHorasDisponiveis]);

  const filtrosVisiveis = filtrosAbertos || modoVisualizacao === "lista";

  const abrirNovoPeloCalendario = (info) => {
    const inicioSelecionado = dayjs(info.start);
    const horarioSelecionado = inicioSelecionado.isValid() && !info.allDay ? inicioSelecionado.format("HH:mm") : "";
    abrirModalNovo({
      data: inicioSelecionado.isValid() ? inicioSelecionado.format("YYYY-MM-DD") : "",
      hora: normalizeTimeValue(horarioSelecionado),
    });
  };

  return (
    <div className="container fade-in">
      <ToastContainer
        position="top-right"
        theme={theme === "dark" ? "dark" : "colored"}
      />
      <div
        className="section-title flex items-center gap-2"
        style={{ justifyContent: "space-between" }}
      >
        <div className="flex items-center gap-2">
          <Calendar /> <span>Agendamentos</span>
        </div>
        <div className="agenda-toolbar">
          <div className="segmented-control" aria-label="Alternar visualização">
            <button
              type="button"
              className={modoVisualizacao === "lista" ? "active" : ""}
              onClick={() => setModoVisualizacao("lista")}
            >
              Lista
            </button>
            <button
              type="button"
              className={modoVisualizacao === "calendario" ? "active" : ""}
              onClick={() => setModoVisualizacao("calendario")}
            >
              Calendário
            </button>
          </div>

          <button
            type="button"
            className={`btn-secondary agenda-filter-toggle${filtrosVisiveis ? " active" : ""}`}
            onClick={() => setFiltrosAbertos((open) => !open)}
            aria-expanded={filtrosVisiveis}
          >
            <SlidersHorizontal size={18} />
            Filtros
            {filtrosVisiveis ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>

          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
            className="btn"
            onClick={() => abrirModalNovo()}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 14px",
              borderRadius: 10,
              fontWeight: 700,
            }}
          >
            <Plus /> Novo Agendamento
          </motion.button>
        </div>
      </div>

      {/* FILTROS */}
      <AnimatePresence initial={false}>
        {filtrosVisiveis && (
          <motion.div
            className={`card-block space-b appointment-filters${
              modoVisualizacao === "calendario" && filtroDataPickerAberto ? " is-date-picker-open" : ""
            }`}
            initial={{ opacity: 0, height: 0, y: -8 }}
            animate={{ opacity: 1, height: "auto", y: 0 }}
            exit={{ opacity: 0, height: 0, y: -8 }}
            transition={{ duration: 0.22 }}
          >
        <div className="appointment-filter-section">
          <div className="filter-section-title">
            Filtrar por data
          </div>
          <div className="appointment-date-filter-grid">
            <CustomSelect
              value={presetFiltro}
              onChange={(e) => {
                const next = e.target.value;
                setPresetFiltro(next);
                if (next === "custom") return;
                const range = getPresetRange(next);
                aplicarFiltroData(range);
              }}
              options={[
                { value: "custom", label: "Período (personalizado)" },
                { value: "today", label: "Hoje" },
                { value: "thisMonth", label: "Este mês" },
                { value: "lastMonth", label: "Mês passado" },
              ]}
              className="appointment-preset-select"
            />
            <PainelDatePicker
              name="dataInicio"
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
              placeholder="Data inicial"
              className="filter-date-picker"
              onCalendarOpen={() => setFiltroDataPickerAberto(true)}
              onCalendarClose={() => setFiltroDataPickerAberto(false)}
            />
            <PainelDatePicker
              name="dataFim"
              value={dataFim}
              onChange={(e) => setDataFim(e.target.value)}
              placeholder="Data final"
              minDate={dataInicio || undefined}
              className="filter-date-picker"
              onCalendarOpen={() => setFiltroDataPickerAberto(true)}
              onCalendarClose={() => setFiltroDataPickerAberto(false)}
            />
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              className="btn filter-action-button"
              onClick={() => aplicarFiltroData({ inicio: dataInicio, fim: dataFim })}
            >
              Filtrar
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              className="btn-secondary filter-action-button"
              onClick={() => {
                setPresetFiltro("custom");
                aplicarFiltroData({ inicio: "", fim: "" });
              }}
            >
              Limpar
            </motion.button>
          </div>
        </div>
        <div className="appointment-search-filter-grid">
          <div
            className="flex items-center filter-field appointment-search-field"
          >
            <Search style={{ color: "var(--muted)", marginRight: "8px" }} />
            <input
              type="text"
              placeholder="Buscar por ID, cliente, profissional ou serviço"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              style={{
                width: "100%",
                border: "none",
                outline: "none",
                background: "transparent",
                color: "var(--text)",
              }}
            />
          </div>

          <CustomSelect
            value={statusFiltro}
            onChange={(e) => setStatusFiltro(e.target.value)}
            className="appointment-status-select"
            options={[
              { value: "todos", label: "Todos os status" },
              { value: "pendente", label: "Pendente" },
              { value: "confirmado", label: "Confirmado" },
              { value: "cancelado", label: "Cancelado" },
              { value: "finalizado", label: "Finalizado" },
            ]}
          />

          <CustomSelect
            value={limite}
            onChange={(e) => {
              setPaginaAtual(1);
              setLimite(Number(e.target.value));
            }}
            className="appointment-limit-select"
            options={[
              { value: 5, label: "5 por página" },
              { value: 10, label: "10 por página" },
              { value: 20, label: "20 por página" },
            ]}
          />
        </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        className={`card-block${modoVisualizacao === "calendario" ? " calendar-card-block" : ""}`}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {loading ? (
          <p className="muted" style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Spinner /> Carregando agendamentos...
          </p>
        ) : modoVisualizacao === "calendario" ? (
          <AgendamentosCalendar
            agendamentos={listaOrdenada}
            estabelecimentoId={estabelecimentoCalendarioId}
            mostrarEncerrados={mostrarEncerradosCalendario}
            onMostrarEncerradosChange={setMostrarEncerradosCalendario}
            onEventClick={abrirDrawer}
            onDateSelect={abrirNovoPeloCalendario}
            onBlockedSlotClick={(mensagem) =>
              toast.info(mensagem, {
                toastId: "calendar-slot-bloqueado",
                autoClose: 3500,
              })
            }
          />
        ) : listaOrdenada.length === 0 ? (
          <p className="muted">Nenhum agendamento encontrado.</p>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table table--mobile-actions">
                <thead>
                  <tr>
                    <th style={getHeaderStyle("id")} onClick={() => handleSort("id")}>
                      ID{renderSortIndicator("id")}
                    </th>
                    <th style={getHeaderStyle("cliente")} onClick={() => handleSort("cliente")}>
                      Cliente{renderSortIndicator("cliente")}
                    </th>
                    <th style={getHeaderStyle("servico")} onClick={() => handleSort("servico")}>
                      Serviço{renderSortIndicator("servico")}
                    </th>
                    <th style={getHeaderStyle("data")} onClick={() => handleSort("data")}>
                      Data / Hora{renderSortIndicator("data")}
                    </th>
                    <th style={getHeaderStyle("status")} onClick={() => handleSort("status")}>
                      Status{renderSortIndicator("status")}
                    </th>
                    <th className="table-actions-header">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {listaOrdenada.map((ag) => (
                    <motion.tr
                      key={ag.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3 }}
                    >
                      <td>{ag.id}</td>
                      <td>{ag.nome_cliente || ag.cliente?.nome}</td>
                      <td>{ag.servico?.nome}</td>
                      <td>{formatarData(ag.data_horario)}</td>
                      <td>
                        <span
                          className={`badge ${
                            ag.status === "pendente"
                              ? "badge-pending"
                              : ag.status === "confirmado"
                                ? "badge-ok"
                              : ag.status === "cancelado"
                                ? "badge-cancel"
                              : ag.status === "finalizado"
                                ? "badge-finalizado"
                              : "badge-today"
                          }`}
                        >
                          {ag.status === "finalizado" ? "Finalizado" : ag.status}
                        </span>
                      </td>
                      <td className="table-actions-cell">
                        <div className="table-actions table-actions--compact">
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.97 }}
                            className="action-icon-btn action-icon-btn--primary"
                            title="Ver detalhes"
                            aria-label={`Ver detalhes do agendamento ${ag.id}`}
                            onClick={() => abrirDrawer(ag)}
                          >
                            <Edit size={18} /> <span className="action-label">Detalhes</span>
                          </motion.button>

                          {String(ag.status || "").toLowerCase() !== "cancelado" && (
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.97 }}
                              className="action-icon-btn action-icon-btn--danger"
                              title="Cancelar agendamento"
                              aria-label={`Cancelar agendamento ${ag.id}`}
                              onClick={() => confirmarCancelamento(ag.id)}
                            >
                              <X size={18} /> <span className="action-label">Cancelar</span>
                            </motion.button>
                          )}

                          {String(ag.status || "").toLowerCase() === "cancelado" && (
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.97 }}
                              className="action-icon-btn action-icon-btn--danger"
                              title="Remover agendamento"
                              aria-label={`Remover agendamento ${ag.id}`}
                              onClick={() => removerAgendamento(ag.id)}
                            >
                              <Trash2 size={18} /> <span className="action-label">Remover</span>
                            </motion.button>
                          )}
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="pagination-info">
              Exibindo <strong>{inicio}</strong>-<strong>{fim}</strong> de{" "}
              <strong>{totalAgendamentos}</strong> agendamentos
            </div>
          </>
        )}
      </motion.div>
      {/* Paginação - sempre exibe os botões se houver mais de 1 item */}
      {modoVisualizacao === "lista" && totalAgendamentos > 0 && (
        <div className="pagination-bar pagination-bar--spaced">
          <button
            className="btn-secondary"
            disabled={paginaAtual === 1}
            onClick={() => setPaginaAtual(paginaAtual - 1)}
            style={{ minWidth: 60, borderRadius: 8, fontWeight: 600, fontSize: 15, opacity: paginaAtual === 1 ? 0.6 : 1 }}
          >Anterior</button>
          {paginationItems.map((item, index) => {
            if (typeof item !== "number") {
              return (
                <span key={`${item}-${index}`} className="pagination-ellipsis" aria-hidden="true">
                  ...
                </span>
              );
            }
            return (
              <button
                key={item}
                className={`btn-secondary${paginaAtual === item ? ' active' : ''}`}
                style={{
                  minWidth: 40,
                  borderRadius: 8,
                  fontWeight: paginaAtual === item ? 700 : 500,
                  fontSize: 15,
                  background: paginaAtual === item ? 'var(--primary-light)' : undefined,
                  color: paginaAtual === item ? 'var(--primary)' : undefined,
                  boxShadow: paginaAtual === item ? '0 2px 8px rgba(37,99,235,0.08)' : undefined,
                  border: paginaAtual === item ? '2px solid var(--primary)' : '1px solid var(--line)',
                  transition: 'all 0.2s',
                }}
                onClick={() => setPaginaAtual(item)}
              >
                {item}
              </button>
            );
          })}
          <button
            className="btn-secondary"
            disabled={paginaAtual === totalPaginas}
            onClick={() => setPaginaAtual(paginaAtual + 1)}
            style={{ minWidth: 60, borderRadius: 8, fontWeight: 600, fontSize: 15, opacity: paginaAtual === totalPaginas ? 0.6 : 1 }}
          >Próxima</button>
        </div>
      )}

      {/* DRAWER (edição) - sem botões cancelar/remover aqui */}
      <AnimatePresence>
        {drawerOpen && agendamentoSelecionado && (() => {
          const statusOriginal = normalizeStatusValue(editOriginal?.status || agendamentoSelecionado.status);
          const statusSelecionado = normalizeStatusValue(agendamentoSelecionado.status);
          const edicaoFinalizada = statusOriginal === "finalizado";
          const edicaoCancelada = statusOriginal === "cancelado";
          const edicaoBloqueada = isStatusTerminal(statusOriginal);
          const bloqueiaDataHora = isStatusTerminal(statusSelecionado);
          const bloqueiaStatusSelect = edicaoBloqueada;
          const bloqueiaObservacoes = edicaoFinalizada;

          const restaurarDataHoraOriginal = () => {
            if (!editOriginal) return;
            const dataHorario = dayjs(editOriginal.data_horario);
            setEditData(dataHorario.isValid() ? dataHorario.format("YYYY-MM-DD") : "");
            setEditHora(dataHorario.isValid() ? dataHorario.format("HH:mm") : "");
          };

          return (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={fecharDrawer}
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.4)",
                backdropFilter: "blur(4px)",
                zIndex: 50,
              }}
            />
            <motion.div
              className="drawer-panel"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 280, damping: 30 }}
              style={{ zIndex: 60 }}
            >
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <h3 style={{ fontWeight: "700", fontSize: "20px" }}>
                  Detalhes do Agendamento
                </h3>
                <button
                  onClick={fecharDrawer}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "var(--muted)",
                    fontSize: "18px",
                  }}
                >
                  <X />
                </button>
              </div>

              {edicaoBloqueada && (
                <div className="agendamento-lock-banner">
                  {edicaoFinalizada
                    ? "Este agendamento está finalizado e não pode mais ser alterado."
                    : "Agendamento cancelado: data, horário e status estão bloqueados. Você ainda pode atualizar as observações."}
                </div>
              )}

              {bloqueiaDataHora && !edicaoBloqueada && (
                <div className="agendamento-lock-banner">
                  Data e horário não podem ser alterados enquanto o status estiver como finalizado ou cancelado.
                </div>
              )}

              <div style={{ marginTop: "20px", lineHeight: "1.8" }}>
                <p>
                  <strong>Cliente:</strong>{" "}
                  {agendamentoSelecionado.nome_cliente || agendamentoSelecionado.cliente?.nome}
                </p>
                <p>
                  <strong>Profissional:</strong>{" "}
                  {agendamentoSelecionado.profissional?.nome}
                </p>
                <p>
                  <strong>Serviço:</strong>{" "}
                  {agendamentoSelecionado.servico?.nome}
                </p>

                <label className="field-label">Data</label>
                <PainelDatePicker
                  name="editData"
                  value={editData}
                  disabled={bloqueiaDataHora}
                  onChange={(e) => {
                    const novaData = e.target.value;
                    setEditData(novaData);
                    if (novaData !== editData) setEditHora("");
                  }}
                  placeholder="Data do agendamento"
                />

                <div style={{ marginTop: 10 }}>
                  <label className="field-label">Hora</label>
                  <FilaZeroTimeSlotPicker
                    value={editHora}
                    onChange={(e) => setEditHora(e.target.value)}
                    slots={bloqueiaDataHora ? [] : editHorasComAtual || []}
                    date={editData}
                    loading={bloqueiaDataHora ? false : editHorasLoading}
                    disabled={bloqueiaDataHora || editHorasLoading || !editData}
                    allowSelectedPast
                    placeholder={
                      bloqueiaDataHora && editHora
                        ? `Horário atual: ${editHora} (bloqueado para este status)`
                        : "Selecione uma data para ver os horários."
                    }
                  />
                </div>

                <label className="field-label" style={{ marginTop: 10 }}>
                  Status
                </label>
                <CustomSelect
                  value={agendamentoSelecionado.status}
                  disabled={bloqueiaStatusSelect}
                  onChange={(e) => {
                    const novoStatus = e.target.value;
                    setAgendamentoSelecionado({
                      ...agendamentoSelecionado,
                      status: novoStatus,
                    });
                    if (isStatusTerminal(novoStatus)) {
                      restaurarDataHoraOriginal();
                    }
                  }}
                  options={[
                    { value: "pendente", label: "Pendente" },
                    { value: "confirmado", label: "Confirmado" },
                    { value: "cancelado", label: "Cancelado" },
                    { value: "finalizado", label: "Finalizado" },
                  ]}
                />

                <label className="field-label" style={{ marginTop: 10 }}>
                  Observações
                </label>
                <textarea
                  className="field field-textarea"
                  value={agendamentoSelecionado.observacoes || ""}
                  disabled={bloqueiaObservacoes}
                  onChange={(event) =>
                    setAgendamentoSelecionado({
                      ...agendamentoSelecionado,
                      observacoes: event.target.value,
                    })
                  }
                  rows={3}
                  placeholder="Observações do atendimento"
                />

              </div>

              <div style={{ textAlign: "right", marginTop: "22px" }}>
                {!edicaoFinalizada && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.97 }}
                    className="btn hover-glow"
                    onClick={salvarAlteracoes}
                    disabled={editSaving}
                  >
                    {editSaving ? <Spinner /> : <Save style={{ marginRight: 6 }} />}
                    {editSaving ? "Salvando..." : "Salvar alterações"}
                  </motion.button>
                )}
                <button className="btn btn-outline" onClick={fecharDrawer}>
                  Fechar
                </button>
              </div>
            </motion.div>
          </>
          );
        })()}
      </AnimatePresence>

      {/* MODAL NOVO AGENDAMENTO */}
      <AnimatePresence>
        {showNovo && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={fecharModalNovo}
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.40)",
                backdropFilter: "blur(3px)",
                zIndex: 70,
              }}
            />
            <motion.div
              initial={{ scale: 0.98, opacity: 0, y: 10 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.98, opacity: 0, y: 10 }}
              transition={{ duration: 0.2 }}
              style={{
                position: "fixed",
                inset: 0,
                display: "grid",
                placeItems: "center",
                zIndex: 80,
                padding: 16,
                overflowY: "auto",
              }}
            >
              <div className="modal-card appointment-modal-card">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "start",
                    gap: 12,
                  }}
                >
                  <div>
                    <div className="modal-title">Novo Agendamento</div>
                    <div className="modal-subtitle">
                      Preencha os campos abaixo para cadastrar.
                    </div>
                  </div>
                  <button
                    onClick={fecharModalNovo}
                    style={{
                      background: "transparent",
                      border: "none",
                      color: "var(--muted)",
                      cursor: "pointer",
                      padding: 4,
                    }}
                  >
                    <X />
                  </button>
                </div>

                <div className="section-split" />

                <div className="modal-scroll">
                  {optsLoading ? (
                    <p className="muted" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <Spinner /> Carregando opções...
                    </p>
                  ) : (
                    <>
                    <div className="appointment-modal-grid appointment-modal-grid--primary">
                      <div className="appointment-modal-field">
                        <label className="field-label">Estabelecimento *</label>
                        <CustomSelect
                          value={novoAgendamento.estabelecimento_id}
                          onChange={(e) => {
                            setNovoAgendamento((s) => ({
                              ...s,
                              estabelecimento_id: e.target.value,
                              profissional_id: "",
                              servico_id: "",
                              hora: "",
                            }));
                            setHorasDisponiveis([]);
                            // clear related errors
                            setErros((prev) => {
                              const p = { ...prev };
                              delete p.estabelecimento_id;
                              return p;
                            });
                          }}
                          onBlur={() => validarCampo('estabelecimento_id')}
                          disabled={Boolean(localStorage.getItem('estabelecimento_id'))}
                          options={[
                            { value: "", label: "Selecione..." },
                            ...estabelecimentos.map((est) => ({
                              value: est.id,
                              label: est.razao_social || est.nome || `Estabelecimento #${est.id}`,
                            })),
                          ]}
                          className={erros.estabelecimento_id ? "input-error" : ""}
                        />
                        {erros.estabelecimento_id && (
                          <div className="error-text">{erros.estabelecimento_id}</div>
                        )}
                      </div>

                      <div className="appointment-modal-field">
                        <label className="field-label">Profissional *</label>
                        <CustomSelect
                          value={novoAgendamento.profissional_id}
                          onChange={(e) => {
                            setNovoAgendamento((s) => ({
                              ...s,
                              profissional_id: e.target.value,
                              hora: "",
                            }));
                            setHorasDisponiveis([]);
                            setErros((prev) => {
                              const p = { ...prev };
                              delete p.profissional_id;
                              return p;
                            });
                          }}
                          onBlur={() => validarCampo('profissional_id')}
                          options={[
                            { value: "", label: "Selecione..." },
                            ...profissionais.map((p) => ({
                              value: p.id,
                              label: p.nome || `Profissional #${p.id}`,
                            })),
                          ]}
                          className={erros.profissional_id ? "input-error" : ""}
                        />
                        {erros.profissional_id && (
                          <div className="error-text">{erros.profissional_id}</div>
                        )}
                      </div>

                      <div className="appointment-modal-field">
                        <label className="field-label">Serviço *</label>
                        <CustomSelect
                          value={novoAgendamento.servico_id}
                          onChange={(e) => {
                            setNovoAgendamento((s) => ({
                              ...s,
                              servico_id: e.target.value,
                              hora: "",
                            }));
                            setHorasDisponiveis([]);
                            setErros((prev) => {
                              const p = { ...prev };
                              delete p.servico_id;
                              return p;
                            });
                          }}
                          onBlur={() => validarCampo('servico_id')}
                          options={[
                            { value: "", label: "Selecione..." },
                            ...servicos.map((s) => ({
                              value: s.id,
                              label: s.nome || `Serviço #${s.id}`,
                            })),
                          ]}
                          className={erros.servico_id ? "input-error" : ""}
                        />
                        {erros.servico_id && (
                          <div className="error-text">{erros.servico_id}</div>
                        )}
                      </div>
                    </div>

                    <div className="appointment-modal-grid appointment-modal-grid--secondary">
                      <div className="appointment-modal-field">
                        <label className="field-label">Nome do cliente *</label>
                        <input
                          type="text"
                          className={`${errClass("nome_cliente")} appointment-text-field`}
                          placeholder="Ex.: João da Silva"
                          value={novoAgendamento.nome_cliente}
                          onChange={(e) => {
                            setNovoAgendamento((s) => ({ ...s, nome_cliente: e.target.value }));
                            setErros((prev) => {
                              const p = { ...prev };
                              delete p.nome_cliente;
                              return p;
                            });
                          }}
                          onBlur={() => validarCampo('nome_cliente')}
                        />
                        {erros.nome_cliente && (
                          <div className="error-text">{erros.nome_cliente}</div>
                        )}
                      </div>

                      <div className="appointment-modal-field">
                        <label className="field-label">Data *</label>
                        <PainelDatePicker
                          name="data"
                          minDate={today}
                          value={novoAgendamento.data}
                          onChange={(e) => {
                            setNovoAgendamento((s) => ({
                              ...s,
                              data: e.target.value,
                              hora: s.data && s.data !== e.target.value ? "" : s.hora,
                            }));
                            setErros((prev) => {
                              const p = { ...prev };
                              delete p.data;
                              return p;
                            });
                            setHorasDisponiveis((slots) =>
                              novoAgendamento.data && novoAgendamento.data !== e.target.value ? [] : slots
                            );
                          }}
                          onBlur={() => validarCampo('data')}
                          error={erros.data}
                          className="new-appointment-date"
                        />
                      </div>
                    </div>

                    <div className="appointment-modal-section">
                      <label className="field-label">Hora *</label>
                      <FilaZeroTimeSlotPicker
                        value={novoAgendamento.hora}
                        onChange={(e) => {
                          setNovoAgendamento((s) => ({ ...s, hora: e.target.value }));
                          setErros((prev) => {
                            const p = { ...prev };
                            delete p.hora;
                            return p;
                          });
                        }}
                        onBlur={() => validarCampo('hora')}
                        slots={horasDisponiveis}
                        date={novoAgendamento.data}
                        loading={horasLoading}
                        disabled={
                          horasLoading ||
                          !novoAgendamento.estabelecimento_id ||
                          !novoAgendamento.profissional_id ||
                          !novoAgendamento.servico_id ||
                          !novoAgendamento.data
                        }
                        error={erros.hora}
                        placeholder="Selecione Profissional, Serviço e Data para ver os horários."
                      />
                      {(!novoAgendamento.profissional_id || !novoAgendamento.servico_id || !novoAgendamento.data) && !horasLoading && (
                        <div className="muted" style={{ marginTop: 8 }}>
                          Selecione Profissional, Serviço e Data para ver os horários.
                        </div>
                      )}
                    </div>

                    <div className="appointment-modal-section">
                      <label className="field-label">Observações</label>
                      <textarea
                        rows={3}
                        className="field field-textarea"
                        placeholder="Opcional"
                        value={novoAgendamento.observacoes}
                        onChange={(e) =>
                          setNovoAgendamento((s) => ({
                            ...s,
                            observacoes: e.target.value,
                          }))
                        }
                      />
                    </div>

                    <div className="resumo-grid">
                      <div>
                        <div style={{ fontWeight: 700, marginBottom: 6 }}>Resumo</div>
                        <ul style={{ color: "var(--muted)", fontSize: 13, lineHeight: 1.6, paddingLeft: 16, margin: 0 }}>
                          <li><strong>Profissional:</strong> {resumoProfissional}</li>
                          <li><strong>Serviço:</strong> {resumoServico}</li>
                          <li><strong>Data/Hora:</strong> {resumoDataHora}</li>
                        </ul>
                      </div>

                      <div className="modal-actions">
                        <button className="btn-secondary" onClick={fecharModalNovo}>
                          Cancelar
                        </button>
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          className="btn"
                          disabled={novoLoading || !isNovoValido}
                          onClick={salvarNovoAgendamento}
                          style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
                        >
                          {novoLoading ? <Spinner /> : <Plus />}
                          Criar Agendamento
                        </motion.button>
                      </div>
                    </div>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Agendamentos;
